import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SeoHead from '../components/SeoHead';
import {
  getPortalAccount,
  getPortalUsage,
  setPortalBudgetCaps,
  updatePortalConfig,
  updatePortalProfile,
  RESEARCH_POSTURE_CLASSES,
  RESEARCH_POSTURE_PERMISSION,
  type BudgetCapsPayload,
  type ConfigUpdatePayload,
  type ResearchPostureView,
  type ResearchTierView,
  type PortalAccountData,
  type PortalUsageData,
  type ProfileUpdatePayload,
} from '../services/prest0nPortal/client';

const PageContainer = styled.main`
  padding: 1.5rem 1rem 7rem;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--brand-canvas);

  @media (min-width: 768px) {
    padding: 2rem 1.5rem 7rem;
  }

  @media (min-width: 1024px) {
    max-width: 1080px;
    padding: 2.5rem;
    min-height: calc(100vh - 160px);
  }
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Eyebrow = styled.div`
  font-size: 0.72rem;
  text-transform: lowercase;
  letter-spacing: 0.08em;
  color: #9a9a92;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 2rem;
  line-height: 1.05;
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  margin: 0 0 0.6rem;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 760px;
  color: #555;
  font-size: 0.98rem;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 1024px) {
    grid-template-columns: 1.2fr 0.8fr;
    align-items: start;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.section`
  background: white;
  border: 1px solid #e6e6e1;
  border-radius: 1.1rem;
  padding: 1.25rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.035);

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const CardTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-weight: 500;
  font-size: 1.2rem;
  letter-spacing: -0.02em;
  color: var(--brand-text);
  text-transform: lowercase;
`;

const CardDescription = styled.p`
  margin: 0 0 1rem;
  color: #6b6b64;
  font-size: 0.92rem;
  line-height: 1.5;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.9rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.span`
  font-size: 0.82rem;
  color: #666;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: lowercase;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #dddcd6;
  border-radius: 0.75rem;
  padding: 0.78rem 0.9rem;
  font-size: 0.95rem;
  background: #fff;

  &:focus {
    outline: none;
    border-color: var(--brand-gold-hover);
    box-shadow: 0 0 0 3px rgba(182, 146, 76, 0.12);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  border: 1px solid #dddcd6;
  border-radius: 0.75rem;
  padding: 0.85rem 0.9rem;
  font-size: 0.95rem;
  background: #fff;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--brand-gold-hover);
    box-shadow: 0 0 0 3px rgba(182, 146, 76, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #dddcd6;
  border-radius: 0.75rem;
  padding: 0.78rem 0.9rem;
  font-size: 0.95rem;
  background: #fff;

  &:focus {
    outline: none;
    border-color: var(--brand-gold-hover);
    box-shadow: 0 0 0 3px rgba(182, 146, 76, 0.12);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const Button = styled.button<{ $secondary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  padding: 0.8rem 1.1rem;
  border: 1px solid ${(p) => (p.$secondary ? '#d6d3cb' : 'var(--brand-text)')};
  background: ${(p) => (p.$secondary ? 'white' : 'var(--brand-text)')};
  color: ${(p) => (p.$secondary ? '#333' : 'white')};
  cursor: pointer;
  font-size: 0.92rem;
  font-weight: 600;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${(p) => (p.$secondary ? '#f7f6f2' : '#222')};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const Notice = styled.div<{ $error?: boolean; $muted?: boolean }>`
  border-radius: 0.85rem;
  padding: 0.85rem 1rem;
  font-size: 0.92rem;
  line-height: 1.5;
  background: ${(p) => (p.$error ? '#fff0ed' : p.$muted ? '#f6f5f1' : '#f1f8f2')};
  color: ${(p) => (p.$error ? '#b23b2a' : p.$muted ? '#666' : '#22663b')};
  border: 1px solid ${(p) => (p.$error ? '#f1c1ba' : p.$muted ? '#ebe8df' : '#cfe6d5')};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
`;

const StatCard = styled.div`
  background: var(--brand-canvas);
  border: 1px solid #ebe8df;
  border-radius: 0.9rem;
  padding: 0.95rem;
`;

const StatValue = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--brand-text);
  letter-spacing: -0.03em;
`;

const StatLabel = styled.div`
  margin-top: 0.25rem;
  color: var(--brand-text-muted);
  font-size: 0.78rem;
  text-transform: lowercase;
  letter-spacing: 0.06em;
`;

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const Chip = styled.button<{ $active: boolean }>`
  border: 1px solid ${(p) => (p.$active ? 'var(--brand-gold-hover)' : '#dddcd6')};
  background: ${(p) => (p.$active ? '#faf3e3' : 'white')};
  color: ${(p) => (p.$active ? '#8d6d2d' : '#444')};
  border-radius: 999px;
  padding: 0.55rem 0.85rem;
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 500;

  &:hover {
    border-color: var(--brand-gold-hover);
  }
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const SessionItem = styled.div`
  border: 1px solid #ece9e0;
  border-radius: 0.9rem;
  padding: 0.9rem 1rem;
  background: var(--brand-canvas);
`;

const SessionTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: #222;
`;

const SessionMeta = styled.div`
  margin-top: 0.25rem;
  color: var(--brand-text-muted);
  font-size: 0.82rem;
  line-height: 1.45;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #f0eee7;
  margin: 1rem 0;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #333;
  font-size: 0.92rem;
  cursor: pointer;
`;

const Small = styled.div`
  color: #8a8a83;
  font-size: 0.82rem;
  line-height: 1.45;
`;

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function isoToLocal(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

const PortalControls: React.FC = () => {
  const [account, setAccount] = useState<PortalAccountData | null>(null);
  const [usage, setUsage] = useState<PortalUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [profileForm, setProfileForm] = useState<ProfileUpdatePayload>({});
  const [configForm, setConfigForm] = useState<ConfigUpdatePayload>({});
  // Research posture (2026-09-02): null until loaded; absent on the server = platform default.
  const [postureForm, setPostureForm] = useState<ResearchPostureView | null>(null);
  const [budgetForm, setBudgetForm] = useState<BudgetCapsPayload>({});
  const [disclaimerText, setDisclaimerText] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountData, usageData] = await Promise.all([
        getPortalAccount(),
        getPortalUsage(),
      ]);
      setAccount(accountData);
      setUsage(usageData);

      setProfileForm({
        firmName: accountData.profile?.firmName || '',
        displayName: accountData.profile?.displayName || '',
        defaultLanguage: (accountData.profile?.defaultLanguage || accountData.config?.defaultLanguage || 'en') as 'en' | 'es',
        accountType: accountData.profile?.accountType || 'solo',
      });

      setConfigForm({
        personaName: accountData.config?.personaName || 'Prest0n',
        welcomeMessage: accountData.config?.welcomeMessage || '',
        defaultLanguage: (accountData.config?.defaultLanguage || accountData.profile?.defaultLanguage || 'en') as 'en' | 'es',
        brandingTone: accountData.config?.brandingTone || 'professional',
        practiceAreas: accountData.config?.practiceAreas || [],
        enabledCapabilities: accountData.config?.enabledCapabilities || [],
        allowedWorkflows: accountData.config?.allowedWorkflows || [],
        disclaimers: accountData.config?.disclaimers || [],
        voiceEnabled: Boolean(accountData.config?.voiceEnabled),
        knowledgePolicy: accountData.config?.knowledgePolicy || 'account-plus-shared',
      });

      setBudgetForm({
        dailyTokenLimit: accountData.config?.budgetCaps?.dailyTokenLimit ?? null,
        monthlyTokenLimit: accountData.config?.budgetCaps?.monthlyTokenLimit ?? null,
      });

      setDisclaimerText((accountData.config?.disclaimers || []).join('\n'));
      setPostureForm(accountData.config?.researchPosture ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portal controls');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 3500);
    return () => window.clearTimeout(id);
  }, [success]);

  const canShowPortal = useMemo(() => {
    return !loading && !error && Boolean(account?.accountId);
  }, [loading, error, account]);

  const toggleArrayValue = useCallback((field: 'enabledCapabilities' | 'allowedWorkflows' | 'practiceAreas', value: string) => {
    setConfigForm((prev) => {
      const current = new Set(prev[field] || []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [field]: Array.from(current) };
    });
  }, []);

  const saveProfile = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await updatePortalProfile(profileForm);
      setSuccess('Account profile saved.');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }, [profileForm, loadAll]);

  const saveConfig = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: ConfigUpdatePayload = {
        ...configForm,
        disclaimers: disclaimerText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      };
      await updatePortalConfig(payload);
      setSuccess('Runtime configuration saved.');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  }, [configForm, disclaimerText, loadAll]);

  const savePosture = useCallback(async () => {
    if (!postureForm) return;
    setSaving(true);
    setError(null);
    try {
      await updatePortalConfig({
        researchPosture: {
          enabled: postureForm.enabled,
          classes: Object.fromEntries(
            Object.entries(postureForm.classes).map(([id, c]) => [id, { minimumTier: c.minimumTier, cite: c.cite }]),
          ),
        },
      });
      setSuccess('Research posture saved.');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save research posture');
    } finally {
      setSaving(false);
    }
  }, [postureForm, loadAll]);

  const saveBudgets = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await setPortalBudgetCaps({
        dailyTokenLimit: budgetForm.dailyTokenLimit ?? null,
        monthlyTokenLimit: budgetForm.monthlyTokenLimit ?? null,
      });
      setSuccess('Budget caps updated.');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget caps');
    } finally {
      setSaving(false);
    }
  }, [budgetForm, loadAll]);

  return (
    <PageContainer>
      <SeoHead
        title="Portal Controls — prest0"
        description="Manage account settings, runtime controls, routing, and usage dashboards for your Prest0n account."
        canonicalUrl="https://prest0.ai/portal-controls"
      />

      <Header>
        <Eyebrow>Phase 5 · customer portal controls</Eyebrow>
        <Title>portal controls</Title>
        <Subtitle>
          Manage account identity, Prest0n runtime behavior, execution routing, and usage visibility from one place.
        </Subtitle>
      </Header>

      {loading && <Notice $muted>Loading account controls…</Notice>}
      {error && <Notice $error>{error}</Notice>}
      {success && <Notice>{success}</Notice>}

      {canShowPortal && account && (
        <Grid>
          <Stack>
            <Card>
              <CardTitle>account identity</CardTitle>
              <CardDescription>
                Core firm metadata used across the portal and runtime context.
              </CardDescription>
              <FormGrid>
                <Field>
                  <Label>firm name</Label>
                  <Input
                    value={profileForm.firmName || ''}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, firmName: e.target.value }))}
                  />
                </Field>
                <Field>
                  <Label>display name</Label>
                  <Input
                    value={profileForm.displayName || ''}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  />
                </Field>
                <Field>
                  <Label>default language</Label>
                  <Select
                    value={profileForm.defaultLanguage || 'en'}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, defaultLanguage: e.target.value as 'en' | 'es' }))}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                  </Select>
                </Field>
                <Field>
                  <Label>account type</Label>
                  <Select
                    value={profileForm.accountType || 'solo'}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, accountType: e.target.value as ProfileUpdatePayload['accountType'] }))}
                  >
                    <option value="solo">Solo</option>
                    <option value="firm">Firm</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="testing">Testing</option>
                  </Select>
                </Field>
              </FormGrid>
              <ButtonRow>
                <Button onClick={saveProfile} disabled={saving}>Save profile</Button>
              </ButtonRow>
            </Card>

            <Card>
              <CardTitle>runtime configuration</CardTitle>
              <CardDescription>
                Control persona, language, tone, policy, and which capabilities are enabled for this account.
              </CardDescription>
              <FormGrid>
                <Field>
                  <Label>persona name</Label>
                  <Input
                    value={configForm.personaName || ''}
                    onChange={(e) => setConfigForm((prev) => ({ ...prev, personaName: e.target.value }))}
                  />
                </Field>
                <Field>
                  <Label>brand tone</Label>
                  <Select
                    value={configForm.brandingTone || 'professional'}
                    onChange={(e) => setConfigForm((prev) => ({ ...prev, brandingTone: e.target.value }))}
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="formal">Formal</option>
                    <option value="conversational">Conversational</option>
                  </Select>
                </Field>
                <Field>
                  <Label>knowledge policy</Label>
                  <Select
                    value={configForm.knowledgePolicy || 'account-plus-shared'}
                    onChange={(e) => setConfigForm((prev) => ({
                      ...prev,
                      knowledgePolicy: e.target.value as 'account-only' | 'account-plus-shared',
                    }))}
                  >
                    <option value="account-plus-shared">Account + shared</option>
                    <option value="account-only">Account only</option>
                  </Select>
                </Field>
                <FullWidth>
                  <Field>
                    <Label>welcome message</Label>
                    <TextArea
                      value={configForm.welcomeMessage || ''}
                      onChange={(e) => setConfigForm((prev) => ({ ...prev, welcomeMessage: e.target.value }))}
                    />
                  </Field>
                </FullWidth>
                <FullWidth>
                  <Field>
                    <Label>disclaimers (one per line)</Label>
                    <TextArea value={disclaimerText} onChange={(e) => setDisclaimerText(e.target.value)} />
                  </Field>
                </FullWidth>
              </FormGrid>

              <Divider />
              <Label>practice areas</Label>
              <ChipGrid>
                {account.meta.allPracticeAreas.map((area) => (
                  <Chip
                    key={area}
                    type="button"
                    $active={Boolean(configForm.practiceAreas?.includes(area))}
                    onClick={() => toggleArrayValue('practiceAreas', area)}
                  >
                    {area}
                  </Chip>
                ))}
              </ChipGrid>

              <Divider />
              <Label>enabled capabilities</Label>
              <ChipGrid>
                {account.meta.allCapabilities.map((capability) => (
                  <Chip
                    key={capability}
                    type="button"
                    $active={Boolean(configForm.enabledCapabilities?.includes(capability))}
                    onClick={() => toggleArrayValue('enabledCapabilities', capability)}
                  >
                    {capability}
                  </Chip>
                ))}
              </ChipGrid>

              <Divider />
              <Label>allowed workflows</Label>
              <ChipGrid>
                {account.meta.allWorkflows.map((workflow) => (
                  <Chip
                    key={workflow}
                    type="button"
                    $active={Boolean(configForm.allowedWorkflows?.includes(workflow))}
                    onClick={() => toggleArrayValue('allowedWorkflows', workflow)}
                  >
                    {workflow}
                  </Chip>
                ))}
              </ChipGrid>

              <Divider />
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={Boolean(configForm.voiceEnabled)}
                  onChange={(e) => setConfigForm((prev) => ({ ...prev, voiceEnabled: e.target.checked }))}
                />
                Enable voice calling for this account
              </CheckboxRow>

              <ButtonRow>
                <Button onClick={saveConfig} disabled={saving}>Save runtime config</Button>
              </ButtonRow>
            </Card>
          </Stack>

          <Stack>
            <Card>
              <CardTitle>research posture</CardTitle>
              <CardDescription>
                What Prest0n researches before answering sensitive questions, and how deeply. Web search = one fast, cited
                lookup; deep research = the full multi-source report. Changing it needs the research-posture permission
                ({RESEARCH_POSTURE_PERMISSION}) — owners have it; an owner grants it to an admin deliberately.
              </CardDescription>
              {postureForm ? (
                <>
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={postureForm.enabled}
                      onChange={(e) => setPostureForm((prev) => (prev ? { ...prev, enabled: e.target.checked } : prev))}
                    />
                    <span>policy on (off = answer every topic directly, no research first)</span>
                  </CheckboxRow>
                  <FormGrid>
                    {RESEARCH_POSTURE_CLASSES.map(({ id, label }) => {
                      const cls = postureForm.classes[id];
                      if (!cls) return null;
                      return (
                        <Field key={id}>
                          <Label>{label}</Label>
                          <Select
                            value={cls.minimumTier}
                            onChange={(e) => setPostureForm((prev) => (prev ? {
                              ...prev,
                              classes: { ...prev.classes, [id]: { ...cls, minimumTier: e.target.value as ResearchTierView } },
                            } : prev))}
                          >
                            <option value="web-search">Web search first (fast, cited)</option>
                            <option value="deep-research">Deep research first</option>
                            <option value="none">No research required</option>
                          </Select>
                          <CheckboxRow>
                            <input
                              type="checkbox"
                              checked={cls.cite}
                              onChange={(e) => setPostureForm((prev) => (prev ? {
                                ...prev,
                                classes: { ...prev.classes, [id]: { ...cls, cite: e.target.checked } },
                              } : prev))}
                            />
                            <span>always cite sources</span>
                          </CheckboxRow>
                        </Field>
                      );
                    })}
                  </FormGrid>
                  <CardDescription>
                    {postureForm.provenance.source === 'owner'
                      ? `Set by this organization (version ${postureForm.provenance.version}, ${postureForm.provenance.updatedAt.slice(0, 10)}).`
                      : 'Platform default — every sensitive class is grounded with web search, escalates to deep research when jurisdiction-specific, time-sensitive, or high-stakes, and always cites.'}
                  </CardDescription>
                  <Button onClick={savePosture} disabled={saving}>Save research posture</Button>
                </>
              ) : (
                <CardDescription>
                  Not provisioned on this account yet — the platform default applies (web search first on every sensitive
                  class, deep research when jurisdiction-specific, time-sensitive, or high-stakes, always cited).
                </CardDescription>
              )}
            </Card>

            <Card>
              <CardTitle>usage dashboard</CardTitle>
              <CardDescription>
                30-day account usage rollup from the account-scoped runtime collections.
              </CardDescription>
              <StatGrid>
                <StatCard>
                  <StatValue>{formatNumber(usage?.totals.turnCount || 0)}</StatValue>
                  <StatLabel>Turns</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{formatNumber(usage?.totals.totalInputTokens || 0)}</StatValue>
                  <StatLabel>Input tokens</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{formatNumber(usage?.totals.totalOutputTokens || 0)}</StatValue>
                  <StatLabel>Output tokens</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{formatUsd(usage?.totals.totalCostUsd || 0)}</StatValue>
                  <StatLabel>Estimated cost</StatLabel>
                </StatCard>
              </StatGrid>
              <Divider />
              <Small>
                Usage window: {usage?.period.from || '—'} → {usage?.period.to || '—'}
              </Small>
            </Card>

            <Card>
              <CardTitle>budget caps</CardTitle>
              <CardDescription>
                Soft limits for runtime usage. Leave blank to remove a cap.
              </CardDescription>
              <FormGrid>
                <Field>
                  <Label>daily token limit</Label>
                  <Input
                    type="number"
                    value={budgetForm.dailyTokenLimit ?? ''}
                    onChange={(e) => setBudgetForm((prev) => ({
                      ...prev,
                      dailyTokenLimit: e.target.value === '' ? null : Number(e.target.value),
                    }))}
                  />
                </Field>
                <Field>
                  <Label>monthly token limit</Label>
                  <Input
                    type="number"
                    value={budgetForm.monthlyTokenLimit ?? ''}
                    onChange={(e) => setBudgetForm((prev) => ({
                      ...prev,
                      monthlyTokenLimit: e.target.value === '' ? null : Number(e.target.value),
                    }))}
                  />
                </Field>
              </FormGrid>
              <ButtonRow>
                <Button onClick={saveBudgets} disabled={saving}>Save budget caps</Button>
              </ButtonRow>
            </Card>

            <Card>
              <CardTitle>execution routing</CardTitle>
              <CardDescription>
                Current VM assignment and runtime release metadata.
              </CardDescription>
              <SessionList>
                <SessionItem>
                  <SessionTitle>{account.vm?.vmName || 'No dedicated VM assigned'}</SessionTitle>
                  <SessionMeta>
                    assignment: {account.vm?.assignmentMode || 'shared'}<br />
                    environment: {account.vm?.environment || 'production'}<br />
                    status: {account.vm?.provisioningStatus || 'unknown'}<br />
                    health: {account.vm?.healthStatus || '—'}
                  </SessionMeta>
                </SessionItem>
                <SessionItem>
                  <SessionTitle>release state</SessionTitle>
                  <SessionMeta>
                    core version: {account.vm?.coreVersion || account.config?.coreReleasePinned || 'latest stable'}<br />
                    overlay version: {account.vm?.overlayVersion || account.config?.overlayVersion || 'default'}<br />
                    migration enabled: {account.config?.migrationEnabled ? 'yes' : 'no'}
                  </SessionMeta>
                </SessionItem>
              </SessionList>
            </Card>

            <Card>
              <CardTitle>recent sessions</CardTitle>
              <CardDescription>
                Latest account-scoped sessions tracked by the backend runtime.
              </CardDescription>
              <SessionList>
                {(usage?.recentSessions || []).length === 0 && (
                  <Notice $muted>No recent sessions recorded yet.</Notice>
                )}
                {(usage?.recentSessions || []).map((session) => (
                  <SessionItem key={session.sessionId}>
                    <SessionTitle>{session.sessionId}</SessionTitle>
                    <SessionMeta>
                      status: {session.status} · mode: {session.executionMode}<br />
                      created: {isoToLocal(session.createdAt)}<br />
                      last turn: {isoToLocal(session.lastTurnAt)}<br />
                      turns: {session.usage?.turnCount ?? 0} · cost: {formatUsd(session.usage?.totalCostUsd ?? 0)}
                    </SessionMeta>
                  </SessionItem>
                ))}
              </SessionList>
            </Card>
          </Stack>
        </Grid>
      )}
    </PageContainer>
  );
};

export default PortalControls;
